import { useEffect, useState, useMemo } from 'react';
import { ppeItemsAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { HardHat, Plus, Edit, Trash2, Search, Package, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const defaultCategories = [
  'Footwear', 'Clothing', 'Hand Protection', 'Eye Protection', 
  'Head Protection', 'Hearing Protection', 'Visibility', 
  'Respiratory', 'Welding PPE', 'Fall Protection', 'Body Protection'
];

export default function PPEManagement() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    sizes_available: '',
    units: '',
    default_replacement_days: 365,
  });

  // Merge + dedupe + sort categories once per data change (not on every render).
  const sortedCategoryOptions = useMemo(
    () => [...new Set([...categories, ...defaultCategories])].sort(),
    [categories]
  );

  useEffect(() => {
    fetchData();
  }, [showActiveOnly, filterCategory]);

  const fetchData = async () => {
    try {
      const [itemsRes, catsRes] = await Promise.all([
        ppeItemsAPI.getAll({ 
          active_only: showActiveOnly,
          category: filterCategory !== 'all' ? filterCategory : undefined
        }),
        ppeItemsAPI.getCategories(),
      ]);
      setItems(itemsRes.data);
      setCategories(catsRes.data.length > 0 ? catsRes.data : defaultCategories);
    } catch (error) {
      toast.error('Failed to load PPE items');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      category: '',
      description: '',
      sizes_available: '',
      units: '',
      default_replacement_days: 365,
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      description: item.description || '',
      sizes_available: item.sizes_available?.join(', ') || '',
      units: item.units?.join(', ') || '',
      default_replacement_days: item.default_replacement_days || 365,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category) {
      toast.error('Name and category are required');
      return;
    }

    const payload = {
      name: formData.name,
      category: formData.category,
      description: formData.description || null,
      sizes_available: formData.sizes_available ? formData.sizes_available.split(',').map(s => s.trim()) : [],
      units: formData.units ? formData.units.split(',').map(s => s.trim()) : [],
      default_replacement_days: parseInt(formData.default_replacement_days) || 365,
    };

    try {
      if (editingItem) {
        await ppeItemsAPI.update(editingItem.id, payload);
        toast.success('PPE item updated');
      } else {
        await ppeItemsAPI.create(payload);
        toast.success('PPE item created');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save PPE item');
    }
  };

  const handleDeactivate = async (item) => {
    if (!window.confirm(`Are you sure you want to ${item.active ? 'deactivate' : 'reactivate'} "${item.name}"?`)) return;

    try {
      await ppeItemsAPI.update(item.id, { active: !item.active });
      toast.success(`PPE item ${item.active ? 'deactivated' : 'reactivated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update PPE item');
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 page-transition" data-testid="ppe-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
            <HardHat className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            PPE Master List
          </h1>
          <p className="text-muted-foreground mt-1">Manage PPE items available for issue</p>
        </div>
        <Button onClick={openCreateModal} data-testid="add-ppe-item-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add PPE Item
        </Button>
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search PPE items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-ppe-items"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full md:w-48" data-testid="filter-ppe-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {sortedCategoryOptions.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={showActiveOnly ? "default" : "outline"}
            onClick={() => setShowActiveOnly(!showActiveOnly)}
            data-testid="toggle-active-only"
          >
            {showActiveOnly ? 'Active Only' : 'Show All'}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="glass-card table-scroll">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Item Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Sizes</TableHead>
              <TableHead>Units</TableHead>
              <TableHead>Replacement (days)</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Loading PPE items...
                </TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No PPE items found
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id} className="table-row-hover" data-testid={`ppe-item-row-${item.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                        <Package className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{item.name}</p>
                        {item.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-secondary/30">{item.category}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {item.sizes_available?.join(', ') || '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {item.units?.join(', ') || '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.default_replacement_days || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn(
                      "border",
                      item.active 
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-slate-500/20 text-slate-400 border-slate-500/30"
                    )}>
                      {item.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(item)} data-testid={`edit-ppe-${item.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleDeactivate(item)}
                        className={item.active ? "text-red-500 hover:text-red-400" : "text-emerald-500 hover:text-emerald-400"}
                        data-testid={`toggle-ppe-${item.id}`}
                      >
                        {item.active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit PPE Item' : 'Add PPE Item'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update the PPE item details' : 'Add a new item to the PPE master list'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Safety Boots"
                data-testid="input-ppe-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({...formData, category: v})}
              >
                <SelectTrigger data-testid="select-ppe-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {defaultCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the item"
                rows={2}
                data-testid="input-ppe-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Available Sizes (comma-separated)</Label>
              <Input
                value={formData.sizes_available}
                onChange={(e) => setFormData({...formData, sizes_available: e.target.value})}
                placeholder="e.g., S, M, L, XL or 6, 7, 8, 9, 10"
                data-testid="input-ppe-sizes"
              />
            </div>

            <div className="space-y-2">
              <Label>Units of Issue (comma-separated)</Label>
              <Input
                value={formData.units}
                onChange={(e) => setFormData({...formData, units: e.target.value})}
                placeholder="e.g., pairs, pieces, boxes"
                data-testid="input-ppe-units"
              />
            </div>

            <div className="space-y-2">
              <Label>Default Replacement Period (days)</Label>
              <Input
                type="number"
                value={formData.default_replacement_days}
                onChange={(e) => setFormData({...formData, default_replacement_days: e.target.value})}
                data-testid="input-ppe-replacement-days"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-ppe-item-btn">
              {editingItem ? 'Update Item' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
